import { useEffect, useState } from "react";
import axios from "axios";
import { SelectOption } from "../types/select";
import { apiV1Endpoint } from "../api/v1";

export type OwnerSelectOption = SelectOption<string> & {
  a?: string;
  _id?: string;
};

export type OwnerFilterConfig = {
  name: string;
  label: string;
  placeholder: string;
  defaultValue: string;
  options: OwnerSelectOption[];
};

interface OwnerData {
  _id: string;
  name: string;
  ownerId: string;
}

interface OwnerResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    success: boolean;
    code: number;
    message: string;
    result: {
      currentPage: number;
      perPage: string;
      totalCount: number;
      pageCount: number;
      data: OwnerData[];
    };
    error: string;
  };
  error: string;
}

export const useGetOwners = () => {
  const [owners, setOwners] = useState<OwnerSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOwners = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get<OwnerResponse>(
          `${apiV1Endpoint}/property/owner?limit=20000&page=1&dropdown=true`,
        );

        const ownerData = response?.data?.result?.result?.data || [];
        const formattedOwners = ownerData.map((owner: OwnerData) => ({
          value: owner.name,
          label: owner.name,
          a: owner.ownerId,
          _id: owner._id,
        }));

        const optionsWithAll: OwnerSelectOption[] = [
          { value: "All Owners", label: "All Owners", a: "" },
          ...formattedOwners,
        ];

        setOwners(optionsWithAll);
      } catch (err) {
        const errorMessage = axios.isAxiosError(err)
          ? err.response?.data?.message || err.message
          : "Failed to fetch owners";
        setError(errorMessage);
        setOwners([{ value: "All Owners", label: "All Owners", a: "" }]);
      } finally {
        setLoading(false);
      }
    };

    fetchOwners();
  }, []);

  return {
    owners,
    loading,
    error,
  };
};
